import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import type { CartItem, CartAddon, MenuItem, OrderStatusResponse, WidgetView } from '../types';

const ORDER_STORAGE_KEY = 'hc:order';
// Drop persisted orders older than this so reloads don't resurrect ancient state.
const ORDER_TTL_MS = 24 * 60 * 60 * 1000;

interface PersistedOrder {
  uuid: string;
  ts: number;
  lastStatus?: OrderStatusResponse | null;
}

function readPersistedOrder(): PersistedOrder | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(ORDER_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedOrder;
    if (!parsed?.uuid || !parsed?.ts) return null;
    if (Date.now() - parsed.ts > ORDER_TTL_MS) {
      window.localStorage.removeItem(ORDER_STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function writePersistedOrder(order: PersistedOrder | null) {
  if (typeof window === 'undefined') return;
  try {
    if (order === null) {
      window.localStorage.removeItem(ORDER_STORAGE_KEY);
    } else {
      window.localStorage.setItem(ORDER_STORAGE_KEY, JSON.stringify(order));
    }
  } catch {
    // ignore quota / disabled storage
  }
}

interface CartContextValue {
  items: CartItem[];
  view: WidgetView;
  setView: (view: WidgetView) => void;
  addItem: (item: MenuItem, addons?: CartAddon[], note?: string) => void;
  removeItem: (uid: string) => void;
  updateQuantity: (uid: string, quantity: number) => void;
  clearCart: () => void;
  totalItems: number;
  totalPrice: number;
  orderUuid: string | null;
  setOrderUuid: (uuid: string | null) => void;
  lastOrderStatus: OrderStatusResponse | null;
  setLastOrderStatus: (status: OrderStatusResponse | null) => void;
  resetOrder: () => void;
}

const CartContext = createContext<CartContextValue | null>(null);

let uidCounter = 0;
function nextUid() {
  return `cart-${++uidCounter}-${Date.now()}`;
}

export function CartProvider({ children }: { children: ReactNode }) {
  const persisted = readPersistedOrder();
  const [items, setItems] = useState<CartItem[]>([]);
  // If we have a persisted in-flight order, jump straight to the waiting view
  // so the customer sees the latest known status (and resumes polling) on reload.
  const [view, setView] = useState<WidgetView>(persisted ? 'waiting' : 'menu');
  const [orderUuid, setOrderUuidState] = useState<string | null>(persisted?.uuid ?? null);
  const [lastOrderStatus, setLastOrderStatusState] = useState<OrderStatusResponse | null>(
    persisted?.lastStatus ?? null
  );

  const setOrderUuid = useCallback((uuid: string | null) => {
    setOrderUuidState(uuid);
    if (uuid === null) {
      setLastOrderStatusState(null);
      writePersistedOrder(null);
    } else {
      writePersistedOrder({ uuid, ts: Date.now(), lastStatus: null });
    }
  }, []);

  const setLastOrderStatus = useCallback((status: OrderStatusResponse | null) => {
    setLastOrderStatusState(status);
    if (!status) return;
    // Update the persisted snapshot in place, keeping the original ts so TTL
    // is measured from order creation, not from the last poll.
    const existing = readPersistedOrder();
    writePersistedOrder({
      uuid: status.uuid,
      ts: existing?.ts ?? Date.now(),
      lastStatus: status,
    });
  }, []);

  const resetOrder = useCallback(() => {
    setOrderUuidState(null);
    setLastOrderStatusState(null);
    writePersistedOrder(null);
  }, []);

  // Cross-tab sync: if another tab clears or updates the order, reflect it here.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    function onStorage(e: StorageEvent) {
      if (e.key !== ORDER_STORAGE_KEY) return;
      const next = readPersistedOrder();
      setOrderUuidState(next?.uuid ?? null);
      setLastOrderStatusState(next?.lastStatus ?? null);
    }
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const addItem = useCallback((menuItem: MenuItem, addons: CartAddon[] = [], note?: string) => {
    // Check if identical item+addons+note combo already exists
    const addonKey = addons.map((a) => a.id).sort().join(',');
    const noteKey = note?.trim() || '';
    setItems((prev) => {
      const existing = prev.find(
        (ci) => ci.item.id === menuItem.id &&
          ci.addons.map((a) => a.id).sort().join(',') === addonKey &&
          (ci.note?.trim() || '') === noteKey
      );
      if (existing) {
        return prev.map((ci) =>
          ci.uid === existing.uid ? { ...ci, quantity: ci.quantity + 1 } : ci
        );
      }
      return [...prev, { uid: nextUid(), item: menuItem, quantity: 1, addons, note: noteKey || undefined }];
    });
  }, []);

  const removeItem = useCallback((uid: string) => {
    setItems((prev) => prev.filter((ci) => ci.uid !== uid));
  }, []);

  const updateQuantity = useCallback((uid: string, quantity: number) => {
    if (quantity <= 0) {
      setItems((prev) => prev.filter((ci) => ci.uid !== uid));
    } else {
      setItems((prev) =>
        prev.map((ci) => (ci.uid === uid ? { ...ci, quantity } : ci))
      );
    }
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
  }, []);

  const totalItems = items.reduce((sum, ci) => sum + ci.quantity, 0);

  const totalPrice = items.reduce((sum, ci) => {
    const addonTotal = ci.addons.reduce((s, a) => s + Number(a.price), 0);
    return sum + (Number(ci.item.price) + addonTotal) * ci.quantity;
  }, 0);

  return (
    <CartContext.Provider
      value={{
        items, view, setView,
        addItem, removeItem, updateQuantity, clearCart,
        totalItems, totalPrice,
        orderUuid, setOrderUuid,
        lastOrderStatus, setLastOrderStatus,
        resetOrder,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
}
